import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Field, OLIVE, errMsg, fmtDate } from "../dpa/dpaUtils";

const Pick = ({ label, value, options, onChange, testId }) => (
  <div className="space-y-1"><div className="text-xs font-medium">{label}</div>
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="rounded-none" data-testid={testId}><SelectValue placeholder="Choose…" /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select></div>
);

const FieldInput = ({ f, value, onChange, criteria }) => {
  if (f.type === "select") return <Pick label={f.label} value={value == null ? "" : String(value)} options={f.options} onChange={onChange} testId={`gov-field-${f.key}`} />;
  if (f.type === "criteria") return (
    <div className="md:col-span-2 space-y-1"><div className="text-xs font-medium">{f.label}</div>
      {criteria.map(([k, l]) => (
        <label key={k} className="flex items-center gap-2 text-sm"><Checkbox checked={(value || []).includes(k)}
          onCheckedChange={(v) => onChange(v ? [...(value || []), k] : (value || []).filter((x) => x !== k))} data-testid={`gov-criteria-${k}`} />{l}</label>
      ))}</div>
  );
  return <div className={f.type === "textarea" ? "md:col-span-2" : ""}><Field label={f.label} type={f.type === "date" ? "date" : f.type === "number" ? "number" : "text"}
    multiline={f.type === "textarea"} value={value == null ? "" : String(value)} onChange={onChange} testId={`gov-field-${f.key}`} /></div>;
};

const RecordDialog = ({ cfg, meta, rec, onClose, onSaved }) => {
  const [f, setF] = useState(rec || { title: "", status: meta.statuses[0], owner: "Dean (Super Admin)", review_date: null, fields: {}, evidence: [] });
  const [note, setNote] = useState("");
  const setField = (k, v) => setF({ ...f, fields: { ...f.fields, [k]: v } });
  const save = async () => {
    const body = { title: f.title, status: f.status, owner: f.owner, review_date: f.review_date, fields: f.fields, evidence: f.evidence, note };
    try {
      const { data } = rec ? await api.put(`/governance/registers/${cfg.key}/${rec.id}`, body) : await api.post(`/governance/registers/${cfg.key}`, body);
      toast.success(`${data.ref} saved`); onSaved();
    } catch (e) { toast.error("Not saved", { description: errMsg(e) }); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="gov-record-dialog">
        <DialogHeader><DialogTitle>{rec ? `${rec.ref} — edit` : `New ${cfg.singular}`}</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="md:col-span-2"><Field label="Title" required value={f.title} onChange={(v) => setF({ ...f, title: v })} testId="gov-title-input" /></div>
          <Pick label="Status" value={f.status} options={meta.statuses} onChange={(v) => setF({ ...f, status: v })} testId="gov-status-select" />
          <Field label="Owner" value={f.owner} onChange={(v) => setF({ ...f, owner: v })} testId="gov-owner-input" />
          <Field label="Next review date" type="date" value={f.review_date} onChange={(v) => setF({ ...f, review_date: v })} testId="gov-review-date" />
          {cfg.form.map((fd) => <FieldInput key={fd.key} f={fd} value={f.fields[fd.key]} onChange={(v) => setField(fd.key, v)} criteria={meta.criteria || []} />)}
        </div>
        <div className="space-y-2">
          <div className="text-xs font-medium">Evidence (where it can be checked)</div>
          {f.evidence.map((e, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
              <Field label="Location / link" value={e.location} onChange={(v) => setF({ ...f, evidence: f.evidence.map((x, j) => (j === i ? { ...x, location: v } : x)) })} testId={`gov-evidence-location-${i}`} />
              <Field label="What it shows" value={e.note} onChange={(v) => setF({ ...f, evidence: f.evidence.map((x, j) => (j === i ? { ...x, note: v } : x)) })} />
              <Button variant="ghost" size="icon" className="mt-5" onClick={() => setF({ ...f, evidence: f.evidence.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" className="rounded-none" onClick={() => setF({ ...f, evidence: [...f.evidence, { location: "", note: "" }] })} data-testid="gov-add-evidence"><Plus className="h-3.5 w-3.5 mr-1" />Add evidence</Button>
        </div>
        {rec && <Field label="Change note (recorded in audit trail)" value={note} onChange={setNote} testId="gov-change-note" />}
        <DialogFooter><Button className="rounded-none text-white" style={OLIVE} onClick={save} data-testid="gov-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RegisterTab = ({ cfg }) => {
  const [meta, setMeta] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get(`/governance/registers/${cfg.key}`).then(({ data }) => setMeta(data));
  useEffect(() => { load(); }, [cfg.key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!meta) return null;
  return (
    <Card className="rounded-none" data-testid={`gov-register-${cfg.key}`}>
      <div className="p-4 flex items-center justify-between border-b" style={{ borderColor: "var(--hi-border)" }}>
        <div className="text-sm text-muted-foreground max-w-3xl">{cfg.intro}</div>
        <Button className="rounded-none text-white" style={OLIVE} onClick={() => setEdit("new")} data-testid={`gov-new-${cfg.key}`}><Plus className="h-4 w-4 mr-1" />New {cfg.singular}</Button>
      </div>
      <div className="overflow-x-auto"><table className="w-full text-sm">
        <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
          <th className="px-3 py-2">Ref</th><th className="px-3 py-2">Title</th>{cfg.columns.map(([l]) => <th key={l} className="px-3 py-2">{l}</th>)}
          <th className="px-3 py-2">Status</th><th className="px-3 py-2">Review</th><th /></tr></thead>
        <tbody>{meta.items.map((r) => (
          <tr key={r.id} className="border-t align-top hover:bg-slate-50" style={{ borderColor: "var(--hi-border)" }} data-testid={`gov-row-${r.ref}`}>
            <td className="px-3 py-2 font-mono text-xs">{r.ref}</td><td className="px-3 py-2 font-medium max-w-xs">{r.title}</td>
            {cfg.columns.map(([l, get]) => <td key={l} className="px-3 py-2 text-xs max-w-xs">{get(r)}</td>)}
            <td className="px-3 py-2 text-xs font-semibold" data-testid={`gov-status-${r.ref}`}>{r.status}</td>
            <td className={`px-3 py-2 text-xs ${r.review_overdue ? "text-red-700 font-semibold" : ""}`}>{fmtDate(r.review_date)}</td>
            <td className="px-3 py-2"><Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEdit(r)} data-testid={`gov-edit-${r.ref}`}><Pencil className="h-3.5 w-3.5" /></Button></td>
          </tr>
        ))}
        {!meta.items.length && <tr><td colSpan={cfg.columns.length + 5} className="px-3 py-6 text-center text-muted-foreground">Nothing recorded yet.</td></tr>}</tbody>
      </table></div>
      {edit && <RecordDialog cfg={cfg} meta={meta} rec={edit === "new" ? null : edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </Card>
  );
};
